import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Confirm,
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  CmsPage,
  CmsPageKind,
  CmsPageMarket,
  TipTapDoc,
} from '../dataProvider';
import { CMS_PAGE_KINDS, KIND_LABEL, SLUG_PATTERN, emptyDoc } from './cmsModel';
import { RichTextEditor } from './RichTextEditor';

/**
 * The CMS page authoring form (D-cms-pages-core). The descriptive-CRUD pattern PLUS the
 * TipTap rich-text body (the novel part). SIMPLE draft / published lifecycle (NOT the
 * page-schema versioned lifecycle): SAVE writes the content and returns the page to
 * DRAFT (editing a live page takes it offline until re-published); PUBLISH (a distinct
 * cms:publish capability) stamps it live. NO versioned history, NO rollback. Authoring
 * gates cms:author, operational market-scoped (market is Market | 'all'). All mutations
 * run through the audited provider; publish routes through the chokepoint.
 */

const CmsPageFormInner = ({ record }: { record?: CmsPage }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  // A page may target a single in-scope market OR both (the RuleMarket idiom).
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  const [title, setTitle] = useState(record?.title ?? '');
  const [slug, setSlug] = useState(record?.slug ?? '');
  const [kind, setKind] = useState<CmsPageKind>(record?.kind ?? 'marketing');
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [body, setBody] = useState<TipTapDoc>(record?.body ?? emptyDoc());
  const [pendingPublish, setPendingPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const canPublish = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:publish' },
  );

  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required';
    if (!SLUG_PATTERN.test(slug)) {
      return 'Slug must be lowercase words joined by hyphens';
    }
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const nowIso = new Date().toISOString();
    try {
      if (isEdit && record) {
        // SAVE returns the page to DRAFT (edits are not live until re-published).
        await dataProvider.update('cmsPages', {
          id: record.id,
          data: {
            title,
            slug,
            kind,
            market,
            body,
            status: 'draft',
            publishedAt: null,
            updatedAt: nowIso,
          },
          previousData: record,
        });
        notify('Draft saved', { type: 'success' });
        redirect('list', 'cmsPages');
      } else {
        const { data } = await dataProvider.create('cmsPages', {
          data: {
            title,
            slug,
            kind,
            market,
            body,
            status: 'draft',
            publishedAt: null,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Page created', { type: 'success' });
        redirect('edit', 'cmsPages', data.id);
      }
    } catch {
      notify('Could not save the page', { type: 'error' });
    }
  };

  // PUBLISH the SAVED page to live (a distinct capability through the chokepoint).
  const confirmPublish = async () => {
    if (!record) return;
    setBusy(true);
    const publishedAt = new Date().toISOString();
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'cms:publish',
        resource: 'cmsPages',
        id: record.id,
        data: { status: 'published', publishedAt },
        previousData: record,
      });
      notify('Page published', { type: 'success' });
      setPendingPublish(false);
      refresh();
    } catch {
      notify('Could not publish the page', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit page' : 'New page'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'cms-title' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              helperText="lowercase-hyphenated"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'cms-slug' }}
            />
            <TextField
              select
              label="Kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as CmsPageKind)}
              margin="dense"
              size="small"
              sx={{ minWidth: 160 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'cms-kind' }}
            >
              {CMS_PAGE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'cms-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Body
          </Typography>
          <RichTextEditor value={body} onChange={setBody} />
        </CardContent>
      </Card>

      {isEdit && record && (
        <Card sx={{ mb: 2 }} data-testid="cms-publish">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">Status</Typography>
              <Chip
                size="small"
                color={record.status === 'published' ? 'success' : 'default'}
                variant={record.status === 'published' ? 'filled' : 'outlined'}
                label={record.status === 'published' ? 'Published' : 'Draft'}
                data-testid="cms-status-chip"
              />
              {record.publishedAt && (
                <Typography variant="caption" color="text.secondary">
                  Live since {record.publishedAt.slice(0, 10)}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              The marketing site serves the published page. Saving an edit
              returns the page to draft until you publish it again.
            </Typography>
            {canPublish && (
              <Button
                variant="contained"
                disabled={busy || record.status === 'published'}
                onClick={() => setPendingPublish(true)}
                data-testid="cms-publish-button"
              >
                Publish
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="cms-save">
          {isEdit ? 'Save draft' : 'Create page'}
        </Button>
        <Button onClick={() => redirect('list', 'cmsPages')}>Cancel</Button>
      </Stack>

      <Confirm
        isOpen={pendingPublish}
        loading={busy}
        title="Publish this page"
        content="This publishes the saved page to live. The marketing site will serve it. Saving a later edit returns it to draft."
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={confirmPublish}
        onClose={() => setPendingPublish(false)}
      />
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const CmsPageCreateView = () => <CmsPageFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const CmsPageEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<CmsPage>('cmsPages', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <CmsPageFormInner record={record} />;
};

export const CmsPageCreate = () => (
  <RequireAccess capability="cms:author">
    <CmsPageCreateView />
  </RequireAccess>
);

export const CmsPageEdit = () => (
  <RequireAccess capability="cms:author">
    <CmsPageEditView />
  </RequireAccess>
);
